/**
 * 公告系统 - API 封装
 * 
 * 提供公告管理相关的 API 接口
 * 
 * @author AI Assistant
 * @created 2025-12-04
 */

import httpRequest from '@/utils/api'
import type {
  Announcement,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  AnnouncementListParams,
  AnnouncementListResponse,
} from '@/types/announcement'

enum API {
  /** 创建公告 */
  createAnnouncement = '/admin/announcements',
  /** 获取公告列表 */
  getAnnouncements = '/admin/announcements',
  /** 获取公告详情 */
  getAnnouncement = '/admin/announcements/:announcementId',
  /** 更新公告 */
  updateAnnouncement = '/admin/announcements/:announcementId',
  /** 删除公告 */
  deleteAnnouncement = '/admin/announcements/:announcementId',
  /** 发布公告 */
  publishAnnouncement = '/admin/announcements/:announcementId/publish',
  /** 撤回公告 */
  unpublishAnnouncement = '/admin/announcements/:announcementId/unpublish',
}

/**
 * 创建公告
 * 
 * @param data 创建公告请求数据
 * @returns 公告详情响应
 */
export const createAnnouncementAPI = (data: CreateAnnouncementRequest) => {
  return httpRequest.post<Announcement>({
    url: API.createAnnouncement,
    data,
  })
}

/**
 * 获取公告列表
 *
 * @param params 查询参数
 * @returns 公告列表响应
 */
export const getAnnouncementsAPI = (params?: AnnouncementListParams) => {
  // 转换参数为 snake_case
  const queryParams: Record<string, any> = {
    page: params?.page || 1,
    limit: params?.limit || 20,
  }

  if (params?.status) {
    queryParams.status = params.status
  }
  if (params?.isPopup !== undefined) {
    queryParams.is_popup = params.isPopup
  }
  if (params?.priority) {
    queryParams.priority = params.priority
  }

  return httpRequest.get<AnnouncementListResponse>({
    url: API.getAnnouncements,
    params: queryParams,
  })
}

/**
 * 获取公告详情
 * 
 * @param announcementId 公告 ID
 * @returns 公告详情响应
 */
export const getAnnouncementAPI = (announcementId: number) => {
  return httpRequest.get<Announcement>({
    url: API.getAnnouncement.replace(':announcementId', String(announcementId)),
  })
}

/**
 * 更新公告
 * 
 * @param announcementId 公告 ID
 * @param data 更新公告请求数据
 * @returns 公告详情响应
 */
export const updateAnnouncementAPI = (
  announcementId: number,
  data: UpdateAnnouncementRequest
) => {
  return httpRequest.put<Announcement>({
    url: API.updateAnnouncement.replace(':announcementId', String(announcementId)),
    data,
  })
}

/**
 * 删除公告
 *
 * @param announcementId 公告 ID
 * @returns 删除结果
 */
export const deleteAnnouncementAPI = (announcementId: number) => {
  return httpRequest.delete<null>({
    url: API.deleteAnnouncement.replace(':announcementId', String(announcementId)),
  })
}

/**
 * 发布公告（草稿 → 已发布）
 *
 * @param announcementId 公告 ID
 * @returns 公告详情响应
 */
export const publishAnnouncementAPI = (announcementId: number) => {
  return httpRequest.post<Announcement>({
    url: API.publishAnnouncement.replace(':announcementId', String(announcementId)),
  })
}

/**
 * 撤回公告（已发布 → 草稿）
 *
 * @param announcementId 公告 ID
 * @returns 公告详情响应
 */
export const unpublishAnnouncementAPI = (announcementId: number) => {
  return httpRequest.post<Announcement>({
    url: API.unpublishAnnouncement.replace(':announcementId', String(announcementId)),
  })
}

